import { useState } from 'react';
import type { OutputCardProps } from './types';
import type { InputAndSliderProps } from './types';
import { numToCurrency, numToPercentage } from './utils/numberUtils';

interface useCalculatorReturnType {
  inputs: Array<InputAndSliderProps>;
  outputs: Array<OutputCardProps>;
  years: string;
  setYears: (value: string) => void;
  totalReturn: string;
}

interface getOutputsArgs {
  appreciation: string;
  downPayment: string;
  insurance: string;
  mortgageInterest: string;
  otherExpenses: string;
  propertyTax: string;
  purchasePrice: string;
  rent: string;
  years: string;
}

interface getOutputsReturnType {
  capRate: string;
  grossYield: string;
  roi: string;
  annualReturn: string;
  totalReturn: string;
}

const _getOutputs = ({
  appreciation,
  downPayment,
  insurance,
  mortgageInterest,
  otherExpenses,
  propertyTax,
  purchasePrice,
  rent,
  years,
}: getOutputsArgs): getOutputsReturnType => {
  // mortgage
  const MONTHS_IN_LOAN = 30 * 12;
  const downPaymentTotal = Number(purchasePrice) * (Number(downPayment) / 100);
  const loanPrincipal = Number(purchasePrice) - downPaymentTotal;
  const monthlyInterestRate = Number(mortgageInterest) / 12 / 100;
  const monthlyMortgagePayment =
    (loanPrincipal * (monthlyInterestRate * (1 + monthlyInterestRate) ** MONTHS_IN_LOAN)) /
    ((1 + monthlyInterestRate) ** MONTHS_IN_LOAN - 1);
  const remainingLoanPrincipal = Math.max(0, loanPrincipal - monthlyMortgagePayment * 12 * Number(years));
  const totalEquity = downPaymentTotal + (loanPrincipal - remainingLoanPrincipal);

  // expenses
  const monthlyTaxes = (Number(purchasePrice) * (Number(propertyTax) / 100)) / 12;
  const monthlyTotalExpenses = Number(otherExpenses) + monthlyMortgagePayment + monthlyTaxes + Number(insurance) / 12;

  // gains
  const appreciatedHomevalue = Number(purchasePrice) * (1 + Number(appreciation) / 100) ** Number(years);
  const cashFlow = Number(rent) * 12 - monthlyTotalExpenses * 12;

  // outputs
  const capRate = cashFlow / appreciatedHomevalue;
  const grossYield = (Number(rent) * 12) / appreciatedHomevalue;
  const roi = cashFlow / totalEquity;
  const annualReturn = cashFlow;
  const totalReturn = appreciatedHomevalue - Number(purchasePrice) + cashFlow * Number(years);

  return {
    capRate: numToPercentage(capRate),
    grossYield: numToPercentage(grossYield),
    roi: numToPercentage(roi),
    annualReturn: numToCurrency(annualReturn),
    totalReturn: numToCurrency(totalReturn),
  };
};

export const testables = {
  getOutputs: _getOutputs,
};

export const useCalculator = (): useCalculatorReturnType => {
  // inputs
  const [years, setYears] = useState('5');
  const [downPayment, setDownPayment] = useState('20');
  const [mortgageInterest, setMortgageInterest] = useState('5');
  const [purchasePrice, setPurchasePrice] = useState('200000');
  const [propertyTax, setPropertyTax] = useState('1');
  const [rent, setRent] = useState('2000');
  const [insurance, setInsurance] = useState('1200');
  const [appreciation, setAppreciation] = useState('1.5');
  const [otherExpenses, setOtherExpenses] = useState('320');

  // outputs
  const { capRate, grossYield, roi, annualReturn, totalReturn } = _getOutputs({
    appreciation,
    downPayment,
    insurance,
    mortgageInterest,
    otherExpenses,
    propertyTax,
    purchasePrice,
    rent,
    years,
  });

  const inputs: Array<InputAndSliderProps> = [
    {
      title: 'Purchase price',
      max: '3000000',
      min: '0',
      step: '50000',
      value: purchasePrice,
      leftAdornment: '$',
      onChange: setPurchasePrice,
      tooltipText:
        'The amount paid by the investor for the rental property. The purchase price can be paid for in cash or be financed through a mortgage lender.',
    },
    {
      title: 'Down payment',
      max: '100',
      min: '0',
      step: '5',
      value: downPayment,
      rightAdornment: '%',
      onChange: setDownPayment,
      tooltipText:
        'A percentage of the purchase price that is paid upfront by the investor. A down payment between 20% and 30% is generally required for a rental property that will be rented out from day one.',
    },
    {
      title: 'Mortgage rate',
      max: '10',
      min: '0',
      step: '0.5',
      value: mortgageInterest,
      rightAdornment: '%',
      onChange: setMortgageInterest,
      tooltipText: 'The annual cost to borrow money from a lender, expressed as a percentage rate.',
    },
    {
      title: 'Property tax',
      max: '2.5',
      min: '0',
      step: '0.1',
      value: propertyTax,
      rightAdornment: '%',
      onChange: setPropertyTax,
      tooltipText:
        'A tax expense paid on owned property. Property tax, which is usually based on the value of the property and land, may fluctuate.',
    },
    {
      title: 'Rental income',
      max: '10000',
      min: '0',
      step: '250',
      value: rent,
      leftAdornment: '$',
      rightAdornment: '/mo',
      onChange: setRent,
      tooltipText: `A tenant's regular monthly payment to a landlord for the use of the property or land. Rent is generally the primary source of income on a rental property. Calculate a rent price for your rental property.`,
    },
    {
      title: 'Appreciation',
      max: '10',
      min: '0',
      step: '0.1',
      value: appreciation,
      rightAdornment: '%',
      onChange: setAppreciation,
      tooltipText:
        'The increase in value of a property over time, expressed as an annual percentage rate. As a home rises in value, the investor can earn profit from the appreciation. The national appreciation value averages at around 3.5% to 3.8% per year.',
    },
    {
      title: 'Insurance',
      max: '2500',
      min: '0',
      step: '100',
      value: insurance,
      leftAdornment: '$',
      rightAdornment: '/yr',
      onChange: setInsurance,
      tooltipText: `Homeowners insurance protects the property owner's liability and insures the residence against damages and losses. The average annual premium on home insurance usually costs less than 1% of the purchase price. With rental property, there may be additional insurance coverage needed.`,
    },
    {
      title: 'Other expenses',
      max: '2500',
      min: '0',
      step: '50',
      leftAdornment: '$',
      rightAdornment: '/mo',
      value: otherExpenses,
      onChange: setOtherExpenses,
      tooltipText:
        'Any additional operating expenses that go into managing a rental property, like repair costs, maintenance costs, property management fees, HOA fees, advertising costs, utility costs or vacancy costs. You can use Zillow Rental Manager to help manage most of your landlording tasks to make getting paid easier.',
    },
  ];

  const outputs: Array<OutputCardProps> = [
    {
      title: 'Gross Yield',
      tooltipText: 'Gross yield on a rental property is the percentage of profit before expenses have been deducted.',
      value: grossYield,
    },
    {
      title: 'Cap Rate',
      tooltipText:
        'Capitalization rate, or cap rate, is the ratio of net operating income (NOI) in relation to the current market value of the home.',
      value: capRate,
    },
    {
      title: '1 Yr Cash ROI',
      tooltipText:
        'The cash-on-cash return, or cash ROI, is the annual rate of return on a rental property based on the cash earned in relation to the cash invested.',
      value: roi,
    },
    {
      title: 'Annual Return',
      tooltipText: 'The gross yield of an investment is its profit before taxes and expenses are deducted.',
      value: annualReturn,
    },
  ];

  return {
    inputs,
    outputs,
    years,
    setYears,
    totalReturn,
  };
};
